'use client';

// One turn in a conversation, and the tool chips under it.

import { memo } from 'react';
import { Markdown } from '../markdown';
import type { Turn } from '../console';

/**
 * Runs of the same tool collapse into one chip with a count. A turn that read
 * eleven files says so in a line instead of eleven identical chips, and the order
 * the tools ran in survives because only *consecutive* repeats fold together.
 */
function chips(tools: string[]): { name: string; count: number }[] {
  const out: { name: string; count: number }[] = [];
  for (const name of tools) {
    const last = out[out.length - 1];
    if (last && last.name === name) last.count += 1;
    else out.push({ name, count: 1 });
  }
  return out;
}

/**
 * One turn in the log.
 *
 * Memoised, and that is the point: a streaming reply re-renders on every delta,
 * and re-parsing the Markdown of every turn above it each time would make a long
 * conversation slower with every message. Only the trailing turn's object
 * identity changes while a turn streams (see `patch`), so only it re-renders.
 *
 * What the two roles get is deliberately different. Claude's replies are
 * Markdown and are rendered as such. What you typed is shown exactly as you
 * typed it — a message is not a document, and quietly reinterpreting someone's
 * own words back at them is worse than leaving the asterisks in.
 */
export const TurnView = memo(function TurnView({ turn, streaming }: { turn: Turn; streaming: boolean }) {
  return (
    <article
      className={`turn turn-${turn.role}${turn.failed ? ' is-failed' : ''}`}
      aria-label={turn.role === 'you' ? 'You' : 'Claude'}
    >
      <div className="turn-body">
        {!!turn.tools?.length && (
          /* `is-live` is what keeps the arrival light on the turn being worked
             on. Without it, reloading into a long conversation would flash every
             chip in it at once, which is a page announcing things that happened
             yesterday. */
          <div className={`tools${streaming ? ' is-live' : ''}`}>
            {chips(turn.tools).map(({ name, count }, j) => (
              /* The count is in the key so a repeat remounts the chip and the
                 arrival plays again. `chips` folds a run of the same tool into
                 one chip with a tally, so without this a Bash after a Bash would
                 change a number in place and light nothing — and a tool call is
                 a tool call whether or not the one before it had the same name. */
              <span key={`${j}-${count}`} className="tool">
                {name}
                {count > 1 && <span className="tool-count">×{count}</span>}
              </span>
            ))}
          </div>
        )}

        {turn.role === 'claude' && !turn.failed ? (
          <Markdown text={turn.text} streaming={streaming} writing={turn.writing} />
        ) : (
          <div className="turn-text">
            {turn.text}
            {streaming && <span className="caret" />}
          </div>
        )}

        {turn.meta?.duration_ms != null && (
          <div className="turn-meta">
            {(turn.meta.duration_ms / 1000).toFixed(1)}s
            {turn.meta.cost_usd != null && ` · $${turn.meta.cost_usd.toFixed(4)}`}
          </div>
        )}
      </div>
    </article>
  );
});

